import { useContext, useEffect, useState } from 'react';
import AppContext, { AppData } from '../../AppContext';
import Modal from '../common/Modal';
import Card from '../common/Card';
import './Insektenschutz.scss';
import ModalOpenIcon from '../common/ModalOpenIcon';
import { validateBreite, validateTiefe, ValidationMessage } from '../../utils/validations';
import { getBreite, getTiefe } from '../../utils/app-data';
import { generateSlickSlider, registerGallery } from '../../utils/slick-slider';
import Artikelgallerie from './Artikelgallerie';

function Insektenschutz() {
  const { appData, setDynamicAppData } = useContext(AppContext);

  const [breite, setBreite] = useState(getBreite(appData));
  const [tiefe, setTiefe] = useState(getTiefe(appData));
  const [validations, setValidations] = useState([] as ValidationMessage[]);

  const checkValidity = (data: AppData): boolean => {
    const checkedValidations = [];
    checkedValidations.push(...validateBreite(data));
    checkedValidations.push(...validateTiefe(data));
    setValidations(checkedValidations);
    return !checkedValidations.find((v) => v.severity === 'error');
  };

  const inputColor = (error: boolean, warning: boolean) => {
    if (error) {
      return 'error';
    }
    if (warning) {
      return 'warning';
    }
    return 'valid';
  };

  const isBreiteError = () => !!validations.find((v) => v.severity === 'error'
    && v.kEigenschaft.includes(appData.static.kEigenschaftBreite));
  const isBreiteWarning = () => !!validations.find((v) => v.severity === 'warning'
    && v.kEigenschaft.includes(appData.static.kEigenschaftBreite));
  const isTiefeError = () => !!validations.find((v) => v.severity === 'error'
    && v.kEigenschaft.includes(appData.static.kEigenschaftTiefe));
  const isTiefeWarning = () => !!validations.find((v) => v.severity === 'warning'
    && v.kEigenschaft.includes(appData.static.kEigenschaftTiefe));

  const inputColorBreite = inputColor(isBreiteError(), isBreiteWarning());
  const inputColorTiefe = inputColor(isTiefeError(), isTiefeWarning());

  useEffect(() => {
    const newAppData = { ...appData };
    newAppData.dynamic.selectedVariationWerte[appData.static.kEigenschaftBreite] = breite;
    newAppData.dynamic.selectedVariationWerte[appData.static.kEigenschaftTiefe] = tiefe;
    const valid = checkValidity(newAppData);

    setDynamicAppData((dynamicAppData) => {
      const newDynamicAppData = { ...dynamicAppData };
      newDynamicAppData.invalid = !valid;
      newDynamicAppData.selectedVariationWerte[appData.static.kEigenschaftBreite] = breite;
      newDynamicAppData.selectedVariationWerte[appData.static.kEigenschaftTiefe] = tiefe;
      return newDynamicAppData;
    });
  }, [tiefe, breite]); // eslint-disable-line react-hooks/exhaustive-deps

  const init = () => {
    generateSlickSlider();
    registerGallery();
  };
  useEffect(() => () => init());

  return (
    <div className="configurator__laengenmass">
      <Card>
        <Artikelgallerie />
        <div className="mt-3 d-flex flex-row justify-content-between align-items-center">
          <h3>Breite und Tiefe</h3>
          <ModalOpenIcon modalId="infoModalInsektenschutz" />
          <Modal
            id="infoModalInsektenschutz"
            title="Insektenschutz"
          >
            <div>
              <b>Maßanleitung</b>
              <p>Um das richtige Maß zu ermitteln, messen Sie bitte das Lichte Maß der Lichtschachtöffnung in der Tiefe,
                sowie in der Breite. Zu diesen ermittelten Maßen addieren Sie den Überstand, den das Gitter haben soll und
                geben diese Maße unserem Konfigurator ein, welcher Ihnen sofort den Preis für das Element anzeigt.
              </p>
              <b>Beispiel</b>
              <p>Außenmaß Gitterrost B x T: 1000 mm x 400 mm
                Sie möchten einen Überstand von 20 mm. Dann rechnen Sie insgesamt 40 mm der Breite und 20 mm der Tiefe hinzu.
                Das entspricht einem Bestellmaß von 1040 mm x 420 mm.
              </p>
            </div>
          </Modal>
        </div>
        <hr />
        <div className="mt-3 d-flex flex-column">
          <div className="inputs">
            <div className="input-group mb-2">
              <div className="input-group-prepend">
                <span className="input-group-text">Breite</span>
              </div>
              <input
                className={`${inputColorBreite} form-control`}
                type="text"
                value={breite}
                placeholder="Breite"
                pattern="^[0-9]*$"
                onChange={(e) => {
                  if (!Number.isNaN(Number(e.target.value))) {
                    setBreite(Number(e.target.value));
                    init();
                  }
                }}
              />
              <div className="input-group-append">
                <span className="input-group-text">mm</span>
              </div>
            </div>
            <div className="input-group mb-2">
              <div className="input-group-prepend">
                <span className="input-group-text">Tiefe</span>
              </div>
              <input
                className={`${inputColorTiefe} form-control`}
                type="text"
                value={tiefe}
                placeholder="Tiefe"
                pattern="^[0-9]*$"
                onChange={(e) => {
                  if (!Number.isNaN(Number(e.target.value))) {
                    setTiefe(Number(e.target.value));
                    init();
                  }
                }}
              />
              <div className="input-group-append">
                <span className="input-group-text">mm</span>
              </div>
            </div>
          </div>
          <div>
            {validations.map((v) => v.messageContent)}
          </div>
        </div>
      </Card>
    </div>
  );
}

export default Insektenschutz;
